/**
 * Widget passenger prefill types and bridges from train-results → booking funnel.
 * - URL param `wpax` (base64url JSON) — works cross-origin (preprod / express iframe)
 * - sessionStorage keyed by `wuid` — same-origin fallback (localhost widget + localhost BF)
 */
import genderEnum from '@/config/genderEnum'

export const WIDGET_PASSENGERS_SESSION_KEY_PREFIX = 'tf_widget_passengers_'
export const WIDGET_PASSENGERS_URL_PARAM = 'wpax'

export interface WidgetPassengerPrefill {
    birthdate?: string
    firstname?: string
    lastname?: string
    gender?: string
    title?: string
}

export interface WidgetPassengersPrefill {
    count: number
    editable: boolean
    min?: number
    max?: number
    list?: WidgetPassengerPrefill[]
}

export function widgetPassengersSessionKey(uuid: string): string {
    return `${WIDGET_PASSENGERS_SESSION_KEY_PREFIX}${uuid}`
}

function fromBase64Url(encoded: string): string {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return decodeURIComponent(escape(atob(padded)))
}

export function decodeWidgetPassengersFromUrl(encoded: string): WidgetPassengersPrefill | null {
    if (!encoded?.trim()) return null
    try {
        const json = fromBase64Url(encoded.trim())
        const parsed = JSON.parse(json) as { passengers?: WidgetPassengersPrefill }
        return parsed?.passengers ?? null
    } catch {
        return null
    }
}

export function readWidgetPassengersFromUrl(params: URLSearchParams): WidgetPassengersPrefill | null {
    const wpax = params.get(WIDGET_PASSENGERS_URL_PARAM)
    if (!wpax) return null
    return decodeWidgetPassengersFromUrl(wpax)
}

export function readWidgetPassengersPrefill(
    params: URLSearchParams,
    uuid: string,
): WidgetPassengersPrefill | null {
    return readWidgetPassengersFromUrl(params) ?? (uuid ? readWidgetPassengersFromSession(uuid) : null)
}

export function readWidgetPassengersFromSession(uuid: string): WidgetPassengersPrefill | null {
    if (!uuid || typeof sessionStorage === 'undefined') return null
    try {
        const raw = sessionStorage.getItem(widgetPassengersSessionKey(uuid))
        if (!raw) return null
        const parsed = JSON.parse(raw) as { passengers?: WidgetPassengersPrefill }
        return parsed?.passengers ?? null
    } catch {
        return null
    }
}

export function hasWidgetPassengerIdentity(slot: WidgetPassengerPrefill | undefined): boolean {
    if (!slot) return false
    return !!(
        slot.firstname?.trim() ||
        slot.lastname?.trim() ||
        slot.birthdate?.trim() ||
        slot.gender?.trim() ||
        slot.title?.trim()
    )
}

export function hasWidgetPassengersPrefill(prefill: WidgetPassengersPrefill | null | undefined): boolean {
    if (!prefill?.list?.length) return false
    return prefill.list.some(hasWidgetPassengerIdentity)
}

export function isWidgetPassengerIdentityComplete(slot: WidgetPassengerPrefill | undefined): boolean {
    if (!slot) return false
    const firstName = slot.firstname?.trim() ?? ''
    const lastName = slot.lastname?.trim() ?? ''
    const birthday = slot.birthdate?.trim() ?? ''
    const gender = titleOrGenderToGender(slot.title, slot.gender)
    return !!(firstName && lastName && birthday && gender && gender !== genderEnum.None)
}

export function titleOrGenderToGender(
    title?: string,
    gender?: string,
): string | null {
    const g = (gender || '').trim().toUpperCase()
    if (g === 'MALE' || g === 'M') return genderEnum.Male
    if (g === 'FEMALE' || g === 'F') return genderEnum.Female

    const t = (title || '').trim().toLowerCase()
    if (t === 'mr' || t === 'm') return genderEnum.Male
    if (t === 'mrs' || t === 'ms' || t === 'mme') return genderEnum.Female

    return null
}

export function widgetSlotToTravellerFields(
    slot: WidgetPassengerPrefill,
): Pick<import('../../../../requests-module/src').Traveller, 'firstName' | 'lastName' | 'birthday' | 'gender' | 'fulfilled'> {
    const firstName = slot.firstname?.trim() || null
    const lastName = slot.lastname?.trim() || null
    const birthday = slot.birthdate?.trim() || ''
    const gender = titleOrGenderToGender(slot.title, slot.gender)
    const fulfilled = !!(firstName && lastName && birthday && gender && gender !== genderEnum.None)

    return {
        firstName,
        lastName,
        birthday,
        gender,
        fulfilled,
    }
}
